import styled, { css, keyframes } from 'styled-components/macro';
import { alpha } from '@material-ui/core';
import { AIRouteJobStatus } from '~/store/modules/routing/routing-interfaces';

const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';
const EASE_SPRING = 'cubic-bezier(0.34, 1.56, 0.64, 1)';

const fabEnter = keyframes`
  from { opacity: 0; transform: scale(0.4) translateY(12px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%   { transform: scale(1); opacity: 0.55; }
  100% { transform: scale(1.65); opacity: 0; }
`;

const shimmer = keyframes`
  from { transform: translateX(-100%); }
  to   { transform: translateX(250%); }
`;

const breathe = keyframes`
  0%, 100% { opacity: 0.45; }
  50%      { opacity: 1; }
`;

const popIn = keyframes`
  0%   { transform: scale(0.6); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
`;

const statusPalette = (theme: any, status: AIRouteJobStatus) => {
  switch (status) {
    case 'completed':
      return theme.palette.success;
    case 'failed':
      return theme.palette.error;
    default:
      return theme.palette.primary;
  }
};

const statusColor = (theme: any, status: AIRouteJobStatus) => statusPalette(theme, status).main;

export const Root = styled.div<{ $leaving: boolean }>`
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 1300;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 16px;
  transition: opacity 200ms ${EASE_OUT}, transform 200ms ${EASE_OUT};

  ${({ $leaving }) =>
    $leaving &&
    css`
      opacity: 0;
      transform: scale(0.9) translateY(8px);
      pointer-events: none;
    `}

  @media (max-width: 600px) {
    right: 16px;
    bottom: 16px;
  }

  @media (prefers-reduced-motion: reduce) {
    &,
    & *,
    & *::before,
    & *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }
`;

export const Panel = styled.div<{ $open: boolean }>`
  width: 336px;
  max-width: calc(100vw - 32px);
  padding: 20px;
  border-radius: 16px;
  background: ${({ theme }) => theme.palette.background.paper};
  border: 1px solid ${({ theme }) => theme.palette.divider};
  box-shadow: ${({ theme }) => theme.shadows[12]};
  transform-origin: bottom right;
  transition: opacity 200ms ${EASE_OUT}, transform 200ms ${EASE_OUT}, visibility 200ms;

  ${({ $open }) =>
    $open
      ? css`
          opacity: 1;
          transform: scale(1) translateY(0);
          visibility: visible;
        `
      : css`
          opacity: 0;
          transform: scale(0.94) translateY(8px);
          visibility: hidden;
          pointer-events: none;
        `}
`;

export const PanelHeader = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
`;

export const PanelTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.text.primary};
`;

export const CloseButton = styled.button`
  position: absolute;
  right: -4px;
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: ${({ theme }) => theme.palette.text.secondary};
  cursor: pointer;
  transition: background 150ms ${EASE_OUT}, color 150ms ${EASE_OUT};

  &:hover {
    background: ${({ theme }) => theme.palette.action.hover};
    color: ${({ theme }) => theme.palette.text.primary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.palette.primary.main};
  }

  svg {
    font-size: 20px;
  }
`;

export const StatusCard = styled.button<{ $status: AIRouteJobStatus; $clickable: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: 12px;
  background: ${({ theme }) => alpha(theme.palette.text.primary, 0.03)};
  color: ${({ theme }) => theme.palette.text.primary};
  font: inherit;
  text-align: left;
  overflow: hidden;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: transform 160ms ${EASE_OUT}, border-color 160ms ${EASE_OUT}, background 160ms ${EASE_OUT};

  ${({ $clickable, $status, theme }) =>
    $clickable &&
    css`
      border-color: ${statusColor(theme, $status)};

      @media (hover: hover) and (pointer: fine) {
        &:hover {
          background: ${theme.palette.action.hover};
        }

        &:hover [data-arrow] {
          transform: translateX(3px);
        }
      }

      &:active {
        transform: scale(0.98);
      }
    `}

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.palette.primary.main};
    outline-offset: 2px;
  }
`;

export const StatusIcon = styled.span<{ $status: AIRouteJobStatus }>`
  position: relative;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  color: ${({ theme, $status }) => statusColor(theme, $status)};
  background: ${({ theme, $status }) => alpha(statusColor(theme, $status), 0.13)};

  svg {
    font-size: 24px;
  }

  ${({ $status }) =>
    ($status === 'completed' || $status === 'failed') &&
    css`
      svg {
        animation: ${popIn} 300ms ${EASE_SPRING} both;
      }
    `}

  ${({ $status }) =>
    $status === 'queued' &&
    css`
      svg {
        animation: ${breathe} 2s ease-in-out infinite;
      }
    `}

  ${({ $status }) =>
    $status === 'processing' &&
    css`
      svg {
        animation: ${spin} 1.4s linear infinite;
      }
    `}
`;

export const StatusText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

export const StatusLabel = styled.span<{ $status: AIRouteJobStatus }>`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme, $status }) => statusColor(theme, $status)};
`;

export const StatusHint = styled.span`
  font-size: 13px;
  line-height: 1.4;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const Arrow = styled.span`
  display: grid;
  place-items: center;
  color: ${({ theme }) => theme.palette.text.secondary};
  transition: transform 160ms ${EASE_OUT};
`;

export const ProgressTrack = styled.span`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
  overflow: hidden;
  background: ${({ theme }) => theme.palette.action.hover};

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    width: 40%;
    background: ${({ theme }) => theme.palette.primary.main};
    border-radius: 3px;
    animation: ${shimmer} 1.6s ${EASE_OUT} infinite;
  }
`;

export const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 14px;
  font-size: 12px;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const FooterActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

export const TextButton = styled.button`
  border: 0;
  padding: 4px 8px;
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.palette.text.secondary};
  font: inherit;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.palette.text.primary};
  }
`;

export const Fab = styled.button<{ $status: AIRouteJobStatus }>`
  position: relative;
  display: grid;
  place-items: center;
  width: 52px;
  height: 52px;
  border: 0;
  border-radius: 50%;
  color: ${({ theme, $status }) => statusPalette(theme, $status).contrastText};
  background: ${({ theme, $status }) => statusColor(theme, $status)};
  box-shadow: ${({ theme }) => theme.shadows[6]};
  cursor: pointer;
  animation: ${fabEnter} 360ms ${EASE_SPRING} both;
  transition: transform 160ms ${EASE_OUT}, background 300ms ${EASE_OUT};

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: scale(1.06);
    }
  }

  &:active {
    transform: scale(0.94);
  }

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.palette.primary.light};
    outline-offset: 3px;
  }

  svg {
    position: relative;
    font-size: 24px;
  }

  /* anel girando enquanto a IA trabalha */
  ${({ $status, theme }) =>
    ($status === 'queued' || $status === 'processing') &&
    css`
      &::before {
        content: '';
        position: absolute;
        inset: -5px;
        border-radius: 50%;
        border: 2px solid transparent;
        border-top-color: ${statusColor(theme, $status)};
        border-right-color: ${statusColor(theme, $status)};
        opacity: 0.8;
        animation: ${spin} ${$status === 'queued' ? '2.4s' : '1s'} linear infinite;
      }
    `}

  /* onda chamando atenção quando concluído */
  ${({ $status, theme }) =>
    $status === 'completed' &&
    css`
      &::before {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: 50%;
        background: ${statusColor(theme, $status)};
        animation: ${pulse} 1.8s ${EASE_OUT} infinite;
      }
    `}
`;

export const Badge = styled.span`
  position: absolute;
  top: 2px;
  right: 2px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${({ theme }) => theme.palette.error.main};
  border: 2px solid ${({ theme }) => theme.palette.background.default};
  animation: ${popIn} 300ms ${EASE_SPRING} both;
`;
